import { useCallback, useEffect, useRef } from "react";
import { useImmer } from "use-immer";

interface UseResizablePanelProps {
  initialSize: number;
  minSize: number | undefined;
  maxSize: number | undefined;
  direction: "horizontal" | "vertical";
  position?: "left" | "right" | "top" | "bottom";
  onResize?: (size: number) => void;
}

export const useResizablePanel = ({
  initialSize,
  minSize,
  maxSize,
  direction,
  position = "left",
  onResize,
}: UseResizablePanelProps) => {
  const [size, setSize] = useImmer(initialSize);
  const [isResizing, setIsResizing] = useImmer(false);
  const startPositionRef = useRef(0);
  const startSizeRef = useRef(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      startPositionRef.current =
        direction === "horizontal" ? e.clientX : e.clientY;
      startSizeRef.current = size;
    },
    [direction, size]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      const currentPosition =
        direction === "horizontal" ? e.clientX : e.clientY;
      let delta = currentPosition - startPositionRef.current;

      // Invert delta for right/bottom positioned panels
      if (position === "right" || position === "bottom") {
        delta = -delta;
      }

      const newSize = Math.max(
        minSize ?? 0,
        Math.min(maxSize ?? Infinity, startSizeRef.current + delta)
      );

      setSize(newSize);
      onResize?.(newSize);
    },
    [isResizing, direction, position, minSize, maxSize, onResize]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      // Prevent text selection while resizing
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.userSelect = "";
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return {
    size,
    isResizing,
    handleMouseDown,
  };
};
